import { Button, LinkButton, cn } from '@cloudflare/kumo'
import type { RecentGuest } from '@hub-wifi/shared'
import { useTranslation } from '../../i18n'
import { initials } from '../home/agenda'
import './guests.css'

export const MAX_FORM_CHIPS = 6

interface BaseProps {
  guests: RecentGuest[]
  /** Wrap onto several lines instead of scrolling sideways. */
  wrap?: boolean
}
type ChipProps =
  | (BaseProps & {
      /** Chips fill the form. */
      onPick: (guest: RecentGuest) => void
      selectedEmail?: string
    })
  | (BaseProps & { onPick?: undefined; selectedEmail?: undefined })

/**
 * Recent guests as chips with an initials circle (Kumo has no avatar).
 * With `onPick` they're buttons (fill the form); without, links that open
 * `/my/guests/new?guest=<email>`. Scrolls sideways on phones.
 */
export function RecentGuestChips({ guests, wrap, onPick, selectedEmail }: ChipProps) {
  const { t } = useTranslation()
  return (
    <ul className={cn('guest-chips', wrap && 'guest-chips--wrap')}>
      {guests.map((g) => {
        const avatar = (
          <span className="home-avatar home-avatar--sm" aria-hidden>
            {initials(g.guestName)}
          </span>
        )
        return (
          <li key={g.guestEmail} className="guest-chips__item">
            {onPick ? (
              <Button
                variant="secondary"
                className="guest-chip"
                icon={avatar}
                aria-pressed={selectedEmail === g.guestEmail}
                aria-label={t('dashboard.inviteAgain.invite', { name: g.guestName })}
                onClick={() => onPick(g)}
              >
                <span className="truncate">{g.guestName}</span>
              </Button>
            ) : (
              <LinkButton
                variant="secondary"
                className="guest-chip"
                icon={avatar}
                href={`/my/guests/new?guest=${encodeURIComponent(g.guestEmail)}`}
                aria-label={t('dashboard.inviteAgain.invite', { name: g.guestName })}
              >
                <span className="truncate">{g.guestName}</span>
              </LinkButton>
            )}
          </li>
        )
      })}
    </ul>
  )
}
